'use client';

import { useState, useTransition } from 'react';
import { flushSync } from 'react-dom';
import { useSearch } from '@/context/listing-search-provider';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { ListingsFacets } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import { ClientBrandConfig } from 'types/brand-config';

import { FiltersButton } from '@/components/filter/filters-button';
import { WhenField } from '@/components/search/fields/when-field';
import { WhoField } from '@/components/search/fields/who-field';
import { WhereField } from '@/components/search/fields/where-field';
import { ChevronLeftIcon } from '@/components/search/icons';
import {
  horizontalFieldClass,
  SEARCH_CONFIG,
  searchButtonHorizontalClass,
  searchButtonStackedClass,
  searchSegmentHeightClass,
} from '@/components/search/search-config';
import Button from '@/components/ui/button';

/** Minimum spinner visibility for fast same-page search updates (pushState). */
const SEARCH_SPINNER_MIN_MS = 300;

export type SearchBarProps = {
  brand: ClientBrandConfig;
  quickSearchTarget: string;
  resorts?: NodeResort[];
  style?: 'gray' | 'white';
  facets?: ListingsFacets;
  showFilters?: boolean;
  searchButtonText?: string;
  showSearchButton?: boolean;
  shouldSubmit?: boolean;
  resortId?: string | null;
  /** Force a specific layout; defaults to responsive desktop/mobile split. */
  layout?: 'horizontal' | 'stacked';
  /** Collapse expanded sticky mobile search; shows back + filter header when stacked. */
  onCollapse?: () => void;
  className?: string;
  lockedFilterKeys?: SearchFiltersValues[];
};

export const SearchBar = ({
  brand,
  quickSearchTarget,
  resorts,
  searchButtonText = 'Search',
  showSearchButton = true,
  shouldSubmit = false,
  resortId,
  layout,
  onCollapse,
  facets,
  className,
  lockedFilterKeys,
}: SearchBarProps) => {
  const { applySearchParams } = useSearch();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isPending, startTransition] = useTransition();
  const isSearching = isNavigating || isPending;
  const displaySearchButton = showSearchButton && !shouldSubmit;

  const { showPropertyListOnSearch, showResortOnSearch } = brand;
  const hasWhere =
    showPropertyListOnSearch || (showResortOnSearch && resorts);

  const fieldOrder = hasWhere
    ? (['when', 'who', 'where'] as const)
    : (['when', 'who'] as const);

  const renderFields = (fieldLayout: 'horizontal' | 'stacked') => {
    const isStacked = fieldLayout === 'stacked';
    const lastIndex = fieldOrder.length - 1;
    const fieldClassName = isStacked
      ? clsx('min-w-0 flex-1', searchSegmentHeightClass)
      : horizontalFieldClass;

    return fieldOrder.map((field, index) => {
      const isLast = index === lastIndex;
      const shared = {
        layout: fieldLayout,
        radius: isStacked
          ? index === 0
            ? ('shell-top' as const)
            : isLast
              ? ('shell-bottom' as const)
              : ('segment' as const)
          : index === 0
            ? ('shell-left' as const)
            : isLast && !displaySearchButton
              ? ('shell-right' as const)
              : ('segment' as const),
        showDivider: !isStacked && !isLast,
        isLastInStack: isStacked && isLast,
        shouldSubmit,
        submitPath: quickSearchTarget,
      };

      switch (field) {
        case 'when':
          return (
            <WhenField
              key="when"
              brand={brand}
              direction={isStacked ? 'vertical' : 'horizontal'}
              numberOfMonths={isStacked ? 12 : 2}
              hideNavigation={isStacked}
              className={fieldClassName}
              {...shared}
            />
          );
        case 'who':
          return (
            <WhoField
              key="who"
              className={fieldClassName}
              {...shared}
            />
          );
        case 'where':
          return (
            <WhereField
              key="where"
              brand={brand}
              resorts={resorts}
              resortId={resortId}
              className={fieldClassName}
              {...shared}
            />
          );
        default:
          return null;
      }
    });
  };

  const handleSearch = () => {
    if (isSearching) {
      return;
    }

    flushSync(() => {
      setIsNavigating(true);
    });
    startTransition(() => {
      applySearchParams(quickSearchTarget, { scroll: true });
    });
    window.setTimeout(() => setIsNavigating(false), SEARCH_SPINNER_MIN_MS);
  };

  const renderSearchButton = (fieldLayout: 'horizontal' | 'stacked') => {
    if (!displaySearchButton) return null;

    return (
      <Button
        loading={isSearching}
        onClick={handleSearch}
        type="button"
        style="primary"
        radius="square"
        className={
          fieldLayout === 'horizontal'
            ? searchButtonHorizontalClass
            : searchButtonStackedClass
        }
      >
        {searchButtonText}
      </Button>
    );
  };

  const renderStickyMobileHeader = () => {
    if (!onCollapse) {
      return null;
    }

    const hasFilters = Boolean(brand.filterCategories);

    return (
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Back"
          onClick={onCollapse}
          className="flex min-h-12 items-center justify-center px-3 text-primary-main hover:text-primary-light"
        >
          <ChevronLeftIcon className="!size-5 text-primary-main" />
        </button>
        {hasFilters && (
          <FiltersButton
            brand={brand}
            listingFacets={facets}
            submitPath={quickSearchTarget}
            lockedFilterKeys={lockedFilterKeys}
          />
        )}
      </div>
    );
  };

  const stackedFieldsShellClass = displaySearchButton
    ? SEARCH_CONFIG.stackedShellGapClass
    : undefined;

  const renderStackedLayout = () => (
    <div className={clsx('flex w-full flex-col gap-2', className)}>
      {renderStickyMobileHeader()}
      <div className={clsx('flex w-full flex-col', stackedFieldsShellClass)}>
        <div
          data-search-fields-shell
          className={clsx(
            'flex w-full flex-col overflow-hidden rounded-[0.75rem] bg-white shadow-md',
            SEARCH_CONFIG.mobileBorder
          )}
        >
          {renderFields('stacked')}
        </div>
        {renderSearchButton('stacked')}
      </div>
    </div>
  );

  if (layout === 'stacked') {
    return renderStackedLayout();
  }

  if (layout === 'horizontal') {
    return (
      <div
        className={clsx(
          'flex w-fit max-w-full flex-row items-stretch rounded-[0.75rem] bg-white shadow-md',
          searchSegmentHeightClass,
          className
        )}
      >
        {renderFields('horizontal')}
        {renderSearchButton('horizontal')}
      </div>
    );
  }

  return (
    <>
      {/* Mobile stacked */}
      <div
        className={clsx(
          'flex w-full flex-col lg:hidden',
          stackedFieldsShellClass,
          className
        )}
      >
        <div
          data-search-fields-shell
          className={clsx(
            'flex w-full flex-col overflow-hidden rounded-[0.75rem] bg-white shadow-md',
            SEARCH_CONFIG.mobileBorder
          )}
        >
          {renderFields('stacked')}
        </div>
        {renderSearchButton('stacked')}
      </div>

      {/* Desktop horizontal */}
      <div className="hidden lg:block">
        <div
          className={clsx(
            'flex w-fit max-w-full flex-row items-stretch rounded-[0.75rem] bg-white shadow-md',
            searchSegmentHeightClass
          )}
        >
          {renderFields('horizontal')}
          {renderSearchButton('horizontal')}
        </div>
      </div>
    </>
  );
};
